import Textfield from '@atlaskit/textfield';
// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import { getFieldType } from '@databases/fields';
import { submitFormOnEnter } from '@databases/form/Form';
import ValidationStatus from '@databases/form/ValidationStatus';
import { Metrics } from '@databases/frontend-metrics';
import { useTypingPerformance } from '@databases/frontend-metrics/useTypingPerformance';
import { messages } from '@databases/intl/messages';
import { getTextDelta, useTextDelta } from '@databases/react/useTextDelta';
import { type DatabaseField, type DatabaseFieldType } from '@databases/types/database';
import { getUniqueFieldName } from '@databases/utils/field-name';
import composeRefs from '@seznam/compose-react-refs';
import { noop } from 'lodash-es';
import { type Op } from 'quill-delta';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

export interface FieldNameEditProps {
  field: DatabaseField<DatabaseFieldType>;
  inputId?: string;
  isValidName: boolean;
  existingFieldNames: string[];
  onChange: (change: { name: string; delta: Op[] }) => void;
}

export const FieldNameEditContainerDiv = styled.div``;

const FieldNameEdit = forwardRef<HTMLInputElement, FieldNameEditProps>(
  function FieldNameEdit(
    { isValidName, inputId, field, existingFieldNames, onChange },
    ref
  ) {
    const { formatMessage } = useIntl();
    const [isEditing, setIsEditing] = useState(false);
    const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

    useTextDelta(
      inputRef,
      useCallback(
        (delta, newValue) => {
          onChange({
            name: newValue,
            delta,
          });
        },
        [onChange]
      )
    );

    const setUniqueFieldName = useCallback(
      (value: string) => {
        const fieldLabel = formatMessage(
          messages['field-settings.field-name-inline-edit-default-text'],
          {
            defaultFieldName: formatMessage(getFieldType(field.type).label),
          }
        );
        const uniqueFieldName = getUniqueFieldName(
          value === '' ? fieldLabel : value,
          existingFieldNames
        );

        onChange({
          name: uniqueFieldName,
          delta: getTextDelta(field.name, uniqueFieldName, field.name.length),
        });
      },
      [formatMessage, field.type, field.name, existingFieldNames, onChange]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => submitFormOnEnter(e),
      []
    );

    const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
      setIsEditing(true);
      // Put cursor to the end of the field
      e.currentTarget.setSelectionRange(
        e.currentTarget.value.length,
        e.currentTarget.value.length
      );
    }, []);

    const handleBlur = useCallback(() => {
      setIsEditing(false);
    }, []);

    useEffect(() => {
      if (!isEditing) {
        setUniqueFieldName(field.name.trim());
      }
    }, [field.name, isEditing, setUniqueFieldName]);

    const typingPerformanceRef = useTypingPerformance<HTMLInputElement>(
      Metrics.MEAN_FIELD_NAME_TYPING
    );

    return (
      <ValidationStatus
        error={
          isValidName
            ? undefined
            : formatMessage(messages['validation.unique-field-name'])
        }
      >
        {(props) => (
          <FieldNameEditContainerDiv>
            <Textfield
              {...props}
              id={inputId}
              autoComplete="off"
              testId="field-name-edit-text-field"
              placeholder={formatMessage(
                messages['field-settings.field-name-placeholder']
              )}
              value={field?.name}
              onChange={noop}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              size={1}
              ref={composeRefs(ref, setInputRef, typingPerformanceRef)}
            />
          </FieldNameEditContainerDiv>
        )}
      </ValidationStatus>
    );
  }
);

export default FieldNameEdit;
